import styled, { css } from 'styled-components';
import color from 'color';
import { colors, fontWeights, fonts, headerSize } from '../../theme/styled';
import { Link } from '../link/link.component';
import downIcon from '../../images/arrow-expand-down.svg';
import { media } from '../../theme/media';
import { navHiddenStyle } from './../../layouts/navigation/navigation.styles';

export const Nav = styled.nav`
  top: ${headerSize.mobile}px;
  width: 100%;
  height: 52px;
  line-height: 52px;
  text-align: left;
  justify-content: flex-start;
  background-color: ${colors.grey};
  color: ${colors.white};
  font-size: 0;
  padding-left: 16px;
  padding-right: 16px;
  white-space: nowrap;
  z-index: 4;
  display: inline-flex;
  ${props => (props.hiddenByScroll ? navHiddenStyle : '')};
  transition: transform 0.5s ease-in-out;

  ${media.tablet`
    text-align: center;
    justify-content: center;
  `};

  ${media.tabletLarge`
    padding-left: 0;
    padding-right: 0;
  `};

  ${media.desktopSmall`
    top: ${headerSize.desktop}px;
  `};

  &:before {
    position: absolute;
    content: '';
    display: block;
    background-color: inherit;
    height: 2px;
    top: -2px;
    left: 0;
    width: 100%;
  }
`;

const mainLinkStyle = css`
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  height: 52px;
  font-size: 14px;
  font-family: ${fonts.smallFont};
  font-weight: ${fontWeights.semiBold};
  font-style: normal;
  font-stretch: normal;
  line-height: 1.14;
  letter-spacing: normal;
  color: ${colors.white};
`;

const activeText = css`
  font-weight: ${fontWeights.bold};
`;

const activeTriangle = css`
  &:after {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${colors.charcoalGrey};
  }
`;

const borderColor = ({ isOpen }) => {
  return isOpen ? color(colors.white).alpha(0.2).string() : 'transparent';
};

export const MainLinkWrapper = styled.li`
  display: inline-block;
  ${media.tabletLarge`
    &:not(:first-child) {
      border-left: 1px solid ${borderColor};
    }
  `};
`;

export const MainLink = styled(Link)`
  position: relative;
  ${mainLinkStyle};
  padding-left: 8px;
  padding-right: 8px;

  display: block;
  line-height: 52px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus {
    outline-offset: -2px;
  }

  ${props => props.isActive && activeText};
  ${props => props.isSubActive && activeTriangle};

  ${media.tablet`
    max-width: 150px;
  `};

  ${media.tabletLarge`
    padding-left: 24px;
    padding-right: 24px;
    min-width: 114px;
    max-width: 200px;
  `};
`;

export const ToggleButton = styled.button`
  background: none;
  border: none;
  display: inline-flex;
  ${mainLinkStyle};
  cursor: pointer;
  padding-left: 8px;
  min-width: 100px;
  text-align: left;
  overflow: hidden;

  &:focus {
    outline-offset: -2px;
  }

  ${media.tabletLarge`
    padding-left: 10px;
    min-width: 115px;
  `};

  &:after {
    content: '';
    display: inline-block;
    width: 44px;
    height: 44px;
    background-image: url(${downIcon});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transition: transform 0.25s ease-in-out;
  }

  ${props =>
    props.isOpen &&
    css`
      &:after {
        transform: rotate(180deg);
      }
    `}
`;

export const Subnavigation = styled.section`
  width: 100%;
  max-width: 860px;
  background-color: ${colors.charcoalGrey};
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  text-align: center;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 3;
`;

export const SubnavLinks = styled.nav`
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-direction: column;

  ${media.tabletLarge`
    flex-direction: row;
    justify-content: flex-start;
  `};
`;

export const List = styled.ul`
  margin: 0;
  padding: 0;
  ${props =>
    props.inline
      ? css`
          display: inline-block;
        `
      : ''}
`;

export const MainList = styled(List)`
  flex-grow: 1;
  overflow: hidden;
  ${media.tablet`
    flex-grow: 0;
    overflow: initial;
  `};
  vertical-align: middle;
  display: inline-flex;
`;

export const SubnavList = styled(List)`
  display: inline-flex;
  width: 100%;

  ${media.tablet`
    width: auto;
  `};
`;

export const TogglableItem = styled.li`
  position: relative;
  list-style: none;
  display: block;

  &:not(:first-child) {
    &:before {
      position: absolute;
      content: '';
      z-index: 1;
      top: 0;
      left: 24px;
      right: 24px;
      height: 1px;
      opacity: 0.1;
      background-color: ${colors.white};
    }
  }
`;

export const SecondaryLinkWrapper = styled.li`
  text-align: left;
`;

export const CategoryColumn = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 10px 0;

  &:not(:first-child) {
    border-left: 1px solid ${color(colors.white).alpha(0.2).string()};
  }
`;

export const SecondaryLink = styled(MainLink)`
  height: auto;
  font-size: 14px;
  min-height: 30px;
  font-weight: ${fontWeights.normal};
  border: none !important;
  line-height: 1.57;
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: left;
  justify-content: flex-start;
  white-space: initial;
  text-overflow: initial;
`;

export const ActiveSubNav = styled.nav`
  z-index: 1;
  font-size: 0;
  width: 100%;
  background-color: ${colors.charcoalGrey};
  height: 53px;
  text-align: left;
  padding-left: 16px;
  padding-right: 16px;
  position: relative;
  ${props => (props.hiddenByScroll ? navHiddenStyle : '')};
  transition: transform 0.5s ease-in-out;

  &:before {
    position: absolute;
    content: '';
    display: block;
    background-color: inherit;
    height: 2px;
    top: 0px;
    left: 0;
    width: 100%;
  }

  ${media.tablet`
    text-align: center;
  `};
`;

export const ActiveSubNavLink = styled(MainLink)`
  font-weight: ${fontWeights.normal};
  ${props =>
    props.isActive &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

export const ActiveSubNavLinkWrapper = styled.li``;

export const FormWrapper = styled.section`
  padding: 24px 47px 26px;
  background-color: #394552;
  min-height: 175px;
  height: auto;
`;
